/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import React, { useEffect } from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";

import {
  MsalProvider,
  useMsal,
  MsalAuthenticationTemplate,
} from "@azure/msal-react";
import { EventType, InteractionType } from "@azure/msal-browser";

import { b2cPolicies } from "./authConfig";
import { PageLayout } from "./components/PageLayout";
import { ViewMessage } from "./pages/ViewMessage";
import { PublicMessage } from "./pages/PublicMessage";
import { Messages } from "./pages/Messages";
import { Home } from "./pages/Home";
import { ImportFiles } from "./pages/ImportFiles";
import { ManageGroups } from "./pages/ManageGroups";
import { ViewGroup } from "./pages/ViewGroup";
import { Search } from "./pages/Search";

import { StateProvider } from "./context/store";
import { isChromeOnIOS } from "./utils";
import "./styles/App.css";
import { LaunchNewMessage } from "./pages/LaunchNewMessage";
import { CAPTURE_TYPE } from "./components/react-record-webcam/src";

const Pages = () => {
  /**
   * useMsal is hook that returns the PublicClientApplication instance,
   * an array of all accounts currently signed in and an inProgress value
   * that tells you what msal is currently doing. For more, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
   */
  const { instance } = useMsal();

  /**
   * Using the event API, you can register an event callback that will do something when an event is emitted.
   * When registering an event callback in a react component you will need to make sure you do 2 things.
   * 1) The callback is registered only once
   * 2) The callback is unregistered before the component unmounts.
   * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/events.md
   */

  useEffect(() => {
    // callback
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_FAILURE) {
        if (
          event.error &&
          event.error.errorMessage.indexOf("AADB2C90118") > -1
        ) {
          if (event.interactionType === InteractionType.Redirect) {
            instance.loginRedirect(b2cPolicies.authorities.forgotPassword);
          } else if (event.interactionType === InteractionType.Popup) {
            instance
              .loginPopup(b2cPolicies.authorities.forgotPassword)
              .catch((e) => {
                return;
              });
          }
        }
      }

      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        if (event?.payload) {
          console.log(event);

          /**
           * We need to reject id tokens that were not issued with the default sign-in policy.
           * "acr" claim in the token tells us what policy is used (NOTE: for new policies (v2.0), use "tfp" instead of "acr").
           * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
           */
          if (
            event.payload.idTokenClaims["acr"] ===
            b2cPolicies.names.forgotPassword
          ) {
            window.alert(
              "Password has been reset successfully. \nPlease sign-in with your new password."
            );
            return instance.logout();
          } else if (
            event.payload.idTokenClaims["acr"] === b2cPolicies.names.editProfile
          ) {
            window.alert(
              "Profile has been edited successfully. \nPlease sign-in again."
            );
            return instance.logout();
          }
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  return (
    <Switch>
      <Route path="/" exact>
        <Home />
      </Route>

      <Route path="/new/">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <Messages />
        </MsalAuthenticationTemplate>
      </Route>

      <Route path="/list/:id?">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <Messages />
        </MsalAuthenticationTemplate>
      </Route>

      <Route path="/view/:id">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <ViewMessage />
        </MsalAuthenticationTemplate>
      </Route>
      <Route path="/public/:id">
        <PublicMessage />
      </Route>

      <Route path="/import/">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <ImportFiles />
        </MsalAuthenticationTemplate>
      </Route>

      <Route path="/search/">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <Search />
        </MsalAuthenticationTemplate>
      </Route>

      <Route path="/manage/groups/">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <ManageGroups />
        </MsalAuthenticationTemplate>
      </Route>

      <Route path="/group/:id">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <ViewGroup />
        </MsalAuthenticationTemplate>
      </Route>

      <Route path="/video/">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <LaunchNewMessage autoRecordType={CAPTURE_TYPE.CAMERA} />
        </MsalAuthenticationTemplate>
      </Route>

      <Route path="/screen/">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <LaunchNewMessage autoRecordType={CAPTURE_TYPE.SCREEN} />
        </MsalAuthenticationTemplate>
      </Route>

      <Route path="/import/">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <LaunchNewMessage autoRecordType={CAPTURE_TYPE.IMPORT} />
        </MsalAuthenticationTemplate>
      </Route>

      <Route path="/audio/">
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <LaunchNewMessage autoRecordType={CAPTURE_TYPE.AUDIO} />
        </MsalAuthenticationTemplate>
      </Route>
    </Switch>
  );
};

const App = ({ instance }) => {
  if (isChromeOnIOS()) {
    return (
      <div className="chrome-on-ios-warning">
        <h2>Please open Firefly in Safari</h2>
        <p>
          This app is optimized for Safari when using iOS. Please copy the URL
          and open it in Safari to ensure the best user experience.
        </p>
      </div>
    );
  }
  return (
    <Router>
      <MsalProvider instance={instance}>
        <StateProvider>
          <PageLayout>
            <Pages />
          </PageLayout>
        </StateProvider>
      </MsalProvider>
    </Router>
  );
};

export default App;
